import {
  MouseEvent as ReactMouseEvent,
  Touch as ReactTouch,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useRef,
} from 'react';

export type DragIntention = 'vertical' | 'horizontal';

export interface DragStartEvent {
  x: number;
  y: number;
}

const DRAG_TOLERANCE = 10;

const determineDragIntention = (
  touch: Touch | ReactMouseEvent | ReactTouch,
  moveStart: DragStartEvent,
): DragIntention | undefined => {
  const { clientX, clientY } = touch;
  const deltaX = Math.abs(moveStart.x - clientX);
  const deltaY = Math.abs(moveStart.y - clientY);

  if (deltaX > deltaY) {
    return 'horizontal';
  }

  if (deltaY > deltaX) {
    return 'vertical';
  }

  return undefined;
};

const isDragEvent = (clientX: number, currentX?: number): boolean => {
  return Boolean(currentX && Math.abs(currentX - clientX) > DRAG_TOLERANCE);
};

const isTouchEvent = (
  event: ReactMouseEvent<Element, MouseEvent> | TouchEvent | ReactTouchEvent,
): event is TouchEvent | ReactTouchEvent => {
  return Boolean(window.TouchEvent && event instanceof TouchEvent) || 'touches' in event;
};

export const useDragHandlers = (
  updateFunction: (clientX: number, dragStart: DragStartEvent) => void,
  dragStartCallback?: () => void | undefined,
  dragEndCallback?: () => void,
  forceDisableMove?: boolean,
): ((e: TouchEvent | ReactMouseEvent) => void) => {
  const dragStart = useRef<DragStartEvent | undefined>();
  const moveIntention = useRef<DragIntention | undefined>();
  const hasListeners = useRef<boolean>(false);

  const handleDragMove = useCallback(
    (e: TouchEvent | ReactMouseEvent) => {
      const event = isTouchEvent(e) ? e.changedTouches[0] : e;

      if (!dragStart.current || forceDisableMove) {
        return;
      }

      if (!moveIntention.current) {
        moveIntention.current = determineDragIntention(event, dragStart.current);
      }

      if (moveIntention.current !== 'horizontal') {
        return;
      }

      const { clientX } = event;

      if (!isDragEvent(clientX, dragStart.current.x)) {
        return;
      }

      if (e.cancelable) {
        e.preventDefault();
      }

      updateFunction(clientX, dragStart.current);
    },
    [forceDisableMove, updateFunction],
  );

  const handleDragEnd = useCallback(() => {
    window.removeEventListener('mousemove', handleDragMove as () => void);
    window.removeEventListener('mouseup', handleDragEnd);

    window.removeEventListener('touchmove', handleDragMove, false);
    window.removeEventListener('touchend', handleDragEnd, false);
    hasListeners.current = false;

    document.body.style.cursor = 'auto';

    if (!dragStart.current) {
      return;
    }

    dragStart.current = undefined;
    moveIntention.current = undefined;
    dragEndCallback?.();
  }, [handleDragMove, dragEndCallback]);

  return useCallback(
    (e: TouchEvent | ReactMouseEvent) => {
      const event = isTouchEvent(e) ? e.changedTouches[0] : e;

      if (event) {
        window.addEventListener('mousemove', handleDragMove as () => void);
        window.addEventListener('mouseup', handleDragEnd);

        window.addEventListener('touchmove', handleDragMove as () => void, {
          passive: false,
        });
        window.addEventListener('touchend', handleDragEnd, {
          passive: false,
        });
        hasListeners.current = true;

        document.body.style.cursor = 'ew-resize';

        dragStart.current = {
          x: event.clientX,
          y: event.clientY,
        };
        dragStartCallback?.();
      }
    },
    [dragStartCallback, handleDragEnd, handleDragMove],
  );
};
